import { type Dispatch, type SetStateAction } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { UserType } from '@econtractor/types';
import { UserSelected } from 'modules/user';
import Editor from 'components/editors/Editor';
import SolarIcon from 'components/icons/SolarIcon';
import { useAuth } from 'context/AuthContext';
import TaskTypeSelect from '../../../shared/TaskTypeSelect';
import TaskCategorySelect from '../../../TaskCategory/TaskCategorySelect';
import type { DirFormState } from '../../model/useOffTaskDir';

interface OffTaskDirGeneralTabProps {
    form: DirFormState;
    setForm: Dispatch<SetStateAction<DirFormState>>;
    saving: boolean;
    showDescription: boolean;
    setShowDescription: (show: boolean) => void;
}

const OffTaskDirGeneralTab = ({
    form,
    setForm,
    saving,
    showDescription,
    setShowDescription,
}: OffTaskDirGeneralTabProps) => {
    const { t } = useTranslation(['Tasks']);
    const { auth } = useAuth();

    const currentUser = (auth?.user || null) as UserType | null;
    const canAssignMyself = (current: UserType | null) =>
        Boolean(currentUser?.ID && current?.ID !== currentUser.ID);
    const assignMyself = (field: 'executor' | 'controller') => {
        if (!currentUser) return;
        setForm((prev) => ({ ...prev, [field]: currentUser }));
    };
    const addMyselfBtn = (field: 'executor' | 'controller', current: UserType | null) => {
        if (!canAssignMyself(current)) return null;
        return (
            <button
                type="button"
                className="btn btn-link p-0 align-baseline fs-12"
                disabled={saving}
                onClick={() => assignMyself(field)}
            >
                <SolarIcon name="person-plus" className="me-1" />
                {t('btnAddMyself')}
            </button>
        );
    };
 
    return (
        <>
            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('labelName')}</label>
                <Form.Control
                    value={form.taskName}
                    disabled={saving}
                    autoFocus
                    placeholder={t('labelName')}
                    onChange={(e) => setForm((prev) => ({ ...prev, taskName: e.target.value }))}
                />
            </Form.Group>
            <div className="mb-3">
                <label className="form-label text-muted">{t('labelDueDate')}</label>
                <div className="task-card-panel task-exec-block">
                <div className={`form-check form-switch ${form.termEnabled ? 'mb-2' : 'mb-0'}`}>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        role="switch"
                        id="dir-task-set-term"
                        checked={form.termEnabled}
                        disabled={saving}
                        onChange={(e) => {
                            const termEnabled = e.target.checked;
                            setForm((prev) => ({
                                ...prev,
                                termEnabled,
                                termDays: termEnabled ? prev.termDays : null,
                            }));
                        }}
                    />
                    <label className="form-check-label" htmlFor="dir-task-set-term">
                        <span className="d-block">{t('labelSetTermDays')}</span>
                        <span className="d-block text-muted fs-12">{t('hintSetTermDays')}</span>
                    </label>
                </div>
                {form.termEnabled && (
                    <div className="ps-4">
                        <InputGroup style={{ maxWidth: 220 }}>
                            <Form.Control
                                autoFocus
                                type="number"
                                min={1}
                                step={1}
                                inputMode="numeric"
                                disabled={saving}
                                value={form.termDays ?? ''}
                                onChange={(e) => {
                                    const raw = e.target.value.trim();
                                    if (!raw) {
                                        setForm((prev) => ({ ...prev, termDays: null }));
                                        return;
                                    }
                                    if (!/^\d+$/.test(raw)) return;
                                    const days = Number(raw);
                                    setForm((prev) => ({ ...prev, termDays: days >= 1 ? days : null }));
                                }}
                            />
                            <InputGroup.Text>{t('labelTermDaysSuffix')}</InputGroup.Text>
                        </InputGroup>
                    </div>
                )}
                </div>
            </div>
            <TaskTypeSelect
                value={form.taskTypeId}
                onChange={(opt) => setForm((prev) => ({ ...prev, taskTypeId: opt?.value ?? null }))}
                disabled={saving}
            />
            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.category')}</label>
                <TaskCategorySelect
                    value={form.categoryId}
                    disabled={saving}
                    onChange={(opt) => setForm((prev) => ({ ...prev, categoryId: opt?.value ?? null }))}
                />
            </Form.Group>
            <Form.Group className="mb-3">
                {showDescription ? (
                    <>
                        <label className="form-label text-muted">{t('labelInfo')}</label>
                        <Editor
                            value={form.taskInfo || ''}
                            onChange={(next: string) => setForm((prev) => ({ ...prev, taskInfo: next }))}
                        />
                    </>
                ) : (
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        onClick={() => setShowDescription(true)}
                    >
                        <SolarIcon name="plus" className="me-1" />
                        {t('btnAddInfo')}
                    </button>
                )}
            </Form.Group>
            <UserSelected
                title={t('labelExecutor')}
                value={form.executor}
                onChange={(user) => setForm((prev) => ({ ...prev, executor: user }))}
                disabled={saving}
                maxHeight={200}
                clearable
                rightElement={addMyselfBtn('executor', form.executor)}
            />
            <UserSelected
                title={t('labelController')}
                value={form.controller}
                onChange={(user) => setForm((prev) => ({ ...prev, controller: user }))}
                disabled={saving}
                maxHeight={200}
                clearable
                rightElement={addMyselfBtn('controller', form.controller)}
            />
        </>
    );
};

export default OffTaskDirGeneralTab;
